import { Box, Collapse, Fade, IconButton, Link, Stack, Typography, useTheme } from '@mui/material'
import React, { useContext, useState } from 'react'
import { EbAuthenticatedAppContext } from '@eidsivabredband/app-framework'
import DeleteIcon from '@mui/icons-material/DeleteOutlined'
import { EventPostDto } from '../../../interfaces/IEvent'
import { toDateTime } from '../../../utils/ValueFormatters'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import ExpandLessIcon from '@mui/icons-material/ExpandLess'
import TypographyWithUrl from '../../../components/data-display/TypographyWithUrls'

interface PostBubbleProps {
  eventPost: EventPostDto
  onDelete: (postId: string) => void
}

function PostBubble({ eventPost, onDelete }: PostBubbleProps) {
  const [isActiveBubble, setActiveBubble] = useState<boolean>(false)
  const theme = useTheme()
  const { context } = useContext(EbAuthenticatedAppContext)
  const [collapse, setCollapse] = React.useState<boolean>()
  const isReply = eventPost.postedBy.id !== context.userInfo?.id
  const contentSizeRef = React.useRef<HTMLDivElement>(null)
  const [contentHeight, setContentHeight] = React.useState<number>(0)

  React.useEffect(() => {
    if (!contentSizeRef.current) return
    setContentHeight(contentSizeRef.current?.clientHeight || 0)
  }, [contentSizeRef])

  const handleCollapseBubble = (e: any) => {
    e.stopPropagation()
    setCollapse((prev) => !prev)
  }

  const handleMouseEnter = () => {
    setActiveBubble(!isActiveBubble)
  }

  const handleMouseLeave = () => {
    setActiveBubble(false)
  }

  const handleDelete = () => {
    onDelete(eventPost.postId)
  }

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: isReply ? 'flex-start' : 'flex-end'
      }}>
      {isReply && (
        <Typography
          color={theme.palette.bubble.metaText}
          sx={{
            fontSize: "13px",
            pl: 1
          }}>
          {eventPost.postedBy.name}
        </Typography>
      )}
      <Box
        onMouseLeave={handleMouseLeave}
        sx={{
          margin: "0 0 1rem 0",
          maxWidth: "95%",
          display: "flex",
          alignItems: 'center'
        }}>
        {!isReply && isActiveBubble && (
          <Box sx={{
            paddingRight: 1
          }}>
            <Fade in={isActiveBubble}>
              <IconButton size="small" onClick={handleDelete}>
                <DeleteIcon />
              </IconButton>
            </Fade>
          </Box>
        )}
        <Box>
          <Box onClick={handleMouseEnter}>
            <Box
              sx={{
                bgcolor: isReply
                  ? isActiveBubble
                    ? theme.palette.bubble.replyActive
                    : theme.palette.bubble.replyInactive
                  : isActiveBubble
                    ? theme.palette.bubble.yourActive
                    : theme.palette.bubble.yourInactive,

                borderRadius: "12px",
                padding: 1.5,
                transition: 'all 200ms ease-in',
                cursor: 'pointer'
              }}>
              {contentHeight > 150 ? (
                <Collapse in={collapse} collapsedSize={150}>
                  <TypographyWithUrl contentref={contentSizeRef}>{eventPost.content}</TypographyWithUrl>
                </Collapse>
              ) : (
                <TypographyWithUrl contentref={contentSizeRef}>{eventPost.content}</TypographyWithUrl>
              )}
              <Stack direction={'row'} spacing={'auto'} sx={{
                pt: 1
              }}>
                {contentHeight > 150 && (
                  <Link
                    color="primary"
                    sx={{
                      color: theme.palette.bubble.metaText,
                      textTransform: 'none',
                      fontSize: '13px',
                      padding: 0,
                      typography: {
                        paddingRight: 10,
                      },
                    }}
                    onClick={(e: any) => handleCollapseBubble(e)}
                  >
                    {collapse ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                  </Link>
                )}
                <Typography
                  color={theme.palette.bubble.metaText}
                  sx={{
                    width: '100%',
                    fontSize: '13px',
                    textAlign: 'right',
                    alignSelf: 'center'
                  }}>
                  {toDateTime(new Date(eventPost.postedOn))}
                </Typography>
              </Stack>
            </Box>
          </Box>
        </Box>
      </Box>
    </Box>
  );
}

export default PostBubble
